import React, { useState, useEffect, useRef } from 'react';
import { ModalContainer, ModalContent, Form, Label, Button, Input } from './StyledComponents';

const PartialPayModal = ({
  isOpen,
  onClose,
  onConfirm,
  transaction,
  initialAmount,
  initialDate,
  initialDescription,
}) => {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const amountRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setAmount(initialAmount != null ? String(initialAmount) : '');
      setDate(initialDate || new Date().toISOString().split('T')[0]);
      setDescription(
        initialDescription ?? transaction?.description ?? ''
      );
      setError('');
      setIsSubmitting(false);
      amountRef.current?.focus();
    }
  }, [isOpen, transaction, initialAmount, initialDate, initialDescription]);

  const handleConfirm = async () => {
    const numericAmount = parseFloat(amount);
    const resolvedDescription = description?.trim() || transaction?.description || '';

    if (!Number.isFinite(numericAmount) || numericAmount === 0) {
      setError('Enter a non-zero amount. Use a negative amount for a refund.');
      return;
    }

    const remainingAmount = Number(transaction?.amount);
    if (numericAmount > 0 && Number.isFinite(remainingAmount) && numericAmount > remainingAmount) {
      setError('The payment cannot exceed the remaining amount of ' + remainingAmount.toFixed(2) + '.');
      return;
    }

    setError('');
    setIsSubmitting(true);
    try {
      await onConfirm(numericAmount, date, resolvedDescription);
      onClose();
    } catch (submitError) {
      console.error('Error applying partial transaction:', submitError);
      setError(submitError?.message || 'Unable to apply the partial transaction. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !transaction) return null;

  return (
    <ModalContainer>
      <ModalContent>
        <Form>
          <Label>Partial Transaction Name:</Label>
          <Input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Label>Amount to Apply:</Label>
          <Input
            ref={amountRef}
            type="number"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setError('');
            }}
            max={transaction.amount}
            step="0.01"
            aria-describedby="partial-amount-help"
          />
          <div id="partial-amount-help" style={{ fontSize: '0.85em', margin: '-4px 0 10px' }}>
            Enter a positive payment or a negative amount for a refund.
          </div>
          <Label>Date:</Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          {error && (
            <div role="alert" style={{ color: '#d32f2f', fontSize: '0.9em', marginBottom: '8px' }}>
              {error}
            </div>
          )}
          <Button type="button" onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting ? 'Applying…' : 'Apply'}
          </Button>
          <Button type="button" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
        </Form>
      </ModalContent>
    </ModalContainer>
  );
};

export default PartialPayModal;
